import {
  BUILTIN_VARIABLE_LOOKUP_POLICIES,
  BUILTIN_LOOKUP_ALIASES,
  type VariableLookupPolicy,
} from 'utils/variableMetadata.generated';
import { hasOwnKey, lookupOwn } from 'utils/typeGuards';

/** Explicit spelling policies take precedence over a deprecated name's canonical policy. */
export const getBuiltinVariableLookupPolicy = (
  name: string
): VariableLookupPolicy | undefined =>
  lookupOwn(BUILTIN_VARIABLE_LOOKUP_POLICIES, name) ??
  lookupOwn(
    BUILTIN_VARIABLE_LOOKUP_POLICIES,
    lookupOwn(BUILTIN_LOOKUP_ALIASES, name)
  );

/**
 * Apply the backend's flat-map lookup policy. Keep supplied blanks distinct from
 * absent values so the editor can preserve its missing-value preview behavior.
 */
export const resolveVariableLookupPolicy = (
  values: Readonly<Record<string, string>>,
  policy: VariableLookupPolicy
): string | undefined => {
  if (
    policy.emptyOverrideKeys?.length &&
    policy.emptyOverrideKeys.every((key) => lookupOwn(values, key) === '')
  ) {
    return '';
  }
  const shadowKey = policy.emptyShadowKey;
  const shadowed =
    shadowKey !== undefined &&
    hasOwnKey(values, shadowKey) &&
    (values[shadowKey] === '' || values[shadowKey] == null);
  let emptyValue: '' | undefined;
  for (const key of policy.lookupKeys) {
    if (shadowed && policy.shadowedLookupKeys?.includes(key)) {
      continue;
    }
    const value = lookupOwn(values, key);
    if (value === '') {
      emptyValue = '';
    } else if (value != null) {
      return value;
    }
  }
  return emptyValue;
};
